/**
 * You May Also Like (Product Recommendations)
 * - Fetches recommended products for the CURRENT product via Shopify's
 *   native Product Recommendations API, using the documented pattern:
 *   request this same section again with product_id + section_id in the
 *   query string, which comes back with `recommendations.performed`
 *   true and the product grid populated — then swap that HTML in.
 * - Once populated (or if it was already populated, e.g. via the
 *   Section Rendering API), sets up the same card behaviors as
 *   Featured Products: hover image auto-scroll, entrance animation,
 *   and Add to Cart via AJAX.
 * - The product row is a horizontal carousel: it auto-scrolls
 *   continuously, pauses the instant a shopper interacts with it
 *   (touch, drag, or hover), and resumes a short while after they
 *   stop. Mobile swiping is native browser scrolling; desktop adds
 *   click-and-drag since there's no touch gesture to rely on there.
 */
(function () {
  function initCarouselAutoplay(root, grid) {
    var enabled = root.dataset.carouselAutoplay === 'true';
    var speed = parseFloat(root.dataset.carouselSpeed) || 0;
    var resumeDelay = parseInt(root.dataset.carouselResumeDelay, 10) || 2000;

    var rafId = null;
    var lastTimestamp = null;
    var isPaused = false;
    var resumeTimer = null;

    function step(timestamp) {
      if (lastTimestamp === null) lastTimestamp = timestamp;
      var delta = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      if (!isPaused) {
        var maxScroll = grid.scrollWidth - grid.clientWidth;
        if (maxScroll > 0) {
          var next = grid.scrollLeft + (speed * delta) / 1000;
          // Loop back to the start once we've scrolled past the end,
          // rather than stopping dead at the last card.
          grid.scrollLeft = next >= maxScroll ? 0 : next;
        }
      }

      rafId = requestAnimationFrame(step);
    }

    function pause() {
      isPaused = true;
      if (resumeTimer) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    }

    function scheduleResume(delay) {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        isPaused = false;
        lastTimestamp = null;
      }, delay === undefined ? resumeDelay : delay);
    }

    function stop() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
      if (resumeTimer) clearTimeout(resumeTimer);
    }

    if (enabled && speed > 0) {
      rafId = requestAnimationFrame(step);
    }

    return { pause: pause, scheduleResume: scheduleResume, stop: stop, enabled: enabled };
  }

  function initDragToScroll(grid, onDragStart, onDragEnd) {
    var isDown = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;

    function suppressNextClick() {
      var handler = function (event) {
        event.preventDefault();
        event.stopPropagation();
        grid.removeEventListener('click', handler, true);
      };
      grid.addEventListener('click', handler, true);
    }

    function endDrag() {
      if (!isDown) return;
      isDown = false;
      grid.classList.remove('is-dragging');
      if (onDragEnd) onDragEnd();
      // If the pointer actually moved (a real drag), stop the click on
      // whatever card link is underneath it from firing/navigating.
      if (moved) suppressNextClick();
    }

    grid.addEventListener('pointerdown', function (event) {
      // Touch already gets native swipe scrolling from the browser —
      // custom drag handling here is only for mouse/pen (desktop).
      if (event.pointerType === 'touch') return;

      isDown = true;
      moved = false;
      startX = event.clientX;
      startScroll = grid.scrollLeft;
      grid.classList.add('is-dragging');
      if (grid.setPointerCapture) {
        try {
          grid.setPointerCapture(event.pointerId);
        } catch (e) {
          /* no-op */
        }
      }
      if (onDragStart) onDragStart();
    });

    grid.addEventListener('pointermove', function (event) {
      if (!isDown) return;
      var dx = event.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      grid.scrollLeft = startScroll - dx;
    });

    grid.addEventListener('pointerup', endDrag);
    grid.addEventListener('pointercancel', endDrag);
    grid.addEventListener('pointerleave', function () {
      if (isDown) endDrag();
    });
  }

  function initCarouselNav(root, grid, carousel) {
    var wrapper = root.querySelector('[data-carousel-wrapper]');
    var prevBtn = root.querySelector('[data-carousel-prev]');
    var nextBtn = root.querySelector('[data-carousel-next]');
    var fadeStart = root.querySelector('[data-carousel-fade-start]');
    var fadeEnd = root.querySelector('[data-carousel-fade-end]');

    if (!wrapper) return;

    function maxScroll() {
      return grid.scrollWidth - grid.clientWidth;
    }

    function updateState() {
      var overflowing = maxScroll() > 2;
      var atStart = grid.scrollLeft <= 2;
      var atEnd = grid.scrollLeft >= maxScroll() - 2;

      if (prevBtn) prevBtn.disabled = !overflowing || atStart;
      if (nextBtn) nextBtn.disabled = !overflowing || atEnd;
      if (fadeStart) fadeStart.classList.toggle('is-hidden', !overflowing || atStart);
      if (fadeEnd) fadeEnd.classList.toggle('is-hidden', !overflowing || atEnd);
    }

    function cardStep() {
      var firstCard = grid.querySelector('[data-product-card]');
      if (!firstCard) return grid.clientWidth;
      var gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
      return firstCard.getBoundingClientRect().width + gap;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        carousel.pause();
        grid.scrollBy({ left: -cardStep(), behavior: 'smooth' });
        carousel.scheduleResume();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        carousel.pause();
        grid.scrollBy({ left: cardStep(), behavior: 'smooth' });
        carousel.scheduleResume();
      });
    }

    grid.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState);
    updateState();
  }

  function maybeShowSwipeHint(root, grid) {
    if (root.dataset.carouselHint !== 'true') return;
    // A moving (autoplaying) row already signals it's interactive —
    // the hint is only for when the row is otherwise static.
    if (root.dataset.carouselAutoplay === 'true') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    setTimeout(function () {
      var maxScroll = grid.scrollWidth - grid.clientWidth;
      if (maxScroll <= 2) return; // nothing to swipe to
      grid.classList.add('is-hinting');
      grid.addEventListener('animationend', function handler() {
        grid.classList.remove('is-hinting');
        grid.removeEventListener('animationend', handler);
      });
    }, 900);
  }

  function initCardBehaviors(root) {
    var grid = root.querySelector('[data-product-grid]');
    if (!grid) return;

    var hoverEnabled = root.dataset.hoverAutoscroll === 'true';
    var hoverSpeed = parseInt(root.dataset.hoverSpeed, 10) || 100;
    var animationEnabled = root.dataset.enableAnimation === 'true';
    var perRowDesktop = parseInt(root.dataset.perRowDesktop, 10) || 5;
    var perRowMobile = parseInt(root.dataset.perRowMobile, 10) || 2;

    /* ---------------- Hover image autoscroll (smooth, continuous) ---------------- */
    function setupHoverCycle(card) {
      if (!hoverEnabled) return;
      var media = card.querySelector('[data-product-media]');
      var track = card.querySelector('[data-product-track]');
      if (!media || !track || track.dataset.fpTrackBound === 'true') return;
      track.dataset.fpTrackBound = 'true';

      var originalImages = track.querySelectorAll('[data-product-image]');
      if (originalImages.length < 2) return;

      var firstClone = originalImages[0].cloneNode(true);
      firstClone.removeAttribute('data-product-image');
      firstClone.setAttribute('aria-hidden', 'true');
      track.appendChild(firstClone);

      var originalCount = originalImages.length;
      var position = 0;
      var lastTimestamp = null;
      var rafId = null;
      var isHovering = false;
      var cachedWidth = 0;
      var wrapPoint = 0;

      function step(timestamp) {
        if (lastTimestamp === null) lastTimestamp = timestamp;
        var delta = timestamp - lastTimestamp;
        lastTimestamp = timestamp;

        position += (hoverSpeed * delta) / 1000;
        if (position >= wrapPoint) {
          position -= wrapPoint;
        }

        track.style.transform = 'translateX(-' + position + 'px)';

        if (isHovering) {
          rafId = requestAnimationFrame(step);
        }
      }

      media.addEventListener('mouseenter', function () {
        isHovering = true;
        lastTimestamp = null;
        // Measured once per hover (not every frame) — reading
        // offsetWidth forces a synchronous layout recalculation, and
        // doing that 60 times a second while scrolling is exactly
        // what was causing the janky/laggy motion.
        cachedWidth = media.offsetWidth;
        wrapPoint = cachedWidth * originalCount;
        track.style.transition = 'none';
        if (!rafId) rafId = requestAnimationFrame(step);
      });

      media.addEventListener('mouseleave', function () {
        isHovering = false;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        position = 0;
        track.style.transition = 'transform 0.4s ease';
        track.style.transform = 'translateX(0)';
      });
    }

    /* ---------------- Entrance animation ---------------- */
    var observer = null;

    function currentPerRow() {
      return window.innerWidth <= 749 ? perRowMobile : perRowDesktop;
    }

    function getStaggerMs() {
      var val = getComputedStyle(root).getPropertyValue('--fp-anim-stagger');
      return parseInt(val, 10) || 0;
    }

    function assignAnimationState(card) {
      var index = parseInt(card.dataset.cardIndex, 10) || 0;
      var perRow = currentPerRow();
      // The row is now a single horizontal line rather than wrapped
      // rows, so alternate direction by position-within-the-visible-
      // window (column) instead of by row number, and cap the stagger
      // delay the same way so it doesn't grow unbounded with more cards.
      var col = index % perRow;
      var fromRight = col % 2 === 0;

      card.classList.remove('fp-from-right', 'fp-from-left', 'fp-in-view');
      card.classList.add('fp-animate-ready');
      card.classList.add(fromRight ? 'fp-from-right' : 'fp-from-left');
      card.style.transitionDelay = (col * getStaggerMs()) + 'ms';
    }

    function observeCard(card) {
      if (!animationEnabled) {
        card.classList.remove('fp-animate-ready');
        return;
      }
      assignAnimationState(card);
      if (observer) observer.observe(card);
    }

    function createObserver() {
      if (!('IntersectionObserver' in window)) return null;
      return new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('fp-in-view');
              observer.unobserve(entry.target);
            }
          });
        },
        // root: grid — cards are checked against the carousel's own
        // scrollable viewport, not the page viewport, since off-screen
        // (unscrolled-to) cards would otherwise be considered "visible".
        { root: grid, threshold: 0.15, rootMargin: '0px' }
      );
    }

    /* ---------------- Add to cart ---------------- */
    function setupAddToCart(card) {
      var btn = card.querySelector('[data-add-to-cart]');
      if (!btn || btn.dataset.fpBound === 'true') return;
      btn.dataset.fpBound = 'true';

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var variantId = btn.dataset.variantId;
        if (!variantId) return;

        var textEl = btn.querySelector('[data-add-btn-text]');
        var originalText = textEl ? textEl.textContent : '';
        btn.disabled = true;
        if (textEl) textEl.textContent = 'Adding...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('Add to cart failed');
            return res.json();
          })
          .then(function () {
            btn.classList.add('is-added');
            if (textEl) textEl.textContent = 'Added!';
            document.dispatchEvent(new CustomEvent('cart:updated'));
            setTimeout(function () {
              btn.classList.remove('is-added');
              if (textEl) textEl.textContent = originalText;
              btn.disabled = false;
            }, 1800);
          })
          .catch(function () {
            if (textEl) textEl.textContent = 'Error';
            setTimeout(function () {
              if (textEl) textEl.textContent = originalText;
              btn.disabled = false;
            }, 1800);
          });
      });
    }

    function initCard(card) {
      setupHoverCycle(card);
      setupAddToCart(card);
      observeCard(card);
    }

    observer = createObserver();
    grid.querySelectorAll('[data-product-card]').forEach(initCard);

    /* ---------------- Carousel: autoplay + manual swipe/drag ---------------- */
    // Stop any previous autoplay loop bound to this section (e.g. one
    // left running against fallback content that's about to be
    // replaced by real recommendations) before starting a new one.
    if (root._ymalCarousel) root._ymalCarousel.stop();

    var carousel = initCarouselAutoplay(root, grid);
    root._ymalCarousel = carousel;

    if (carousel.enabled) {
      // Any interaction — touch, drag, or just hovering with a mouse —
      // pauses autoplay immediately and schedules a resume once it ends.
      grid.addEventListener('pointerenter', carousel.pause);
      grid.addEventListener('pointerleave', function () {
        carousel.scheduleResume();
      });
      grid.addEventListener(
        'touchstart',
        carousel.pause,
        { passive: true }
      );
      grid.addEventListener(
        'touchend',
        function () {
          carousel.scheduleResume();
        },
        { passive: true }
      );
    }

    initDragToScroll(
      grid,
      function () {
        carousel.pause();
      },
      function () {
        carousel.scheduleResume();
      }
    );

    initCarouselNav(root, grid, carousel);
    maybeShowSwipeHint(root, grid);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        grid.querySelectorAll('[data-product-card]').forEach(function (card) {
          if (!card.classList.contains('fp-in-view')) assignAnimationState(card);
        });
      }, 150);
    });
  }

  function fetchRealRecommendations(root) {
    var sectionId = root.dataset.sectionId;
    var productId = root.dataset.productId;
    var limit = root.dataset.limit || 10;
    var baseUrl = root.dataset.recommendationsUrl;

    if (!baseUrl || !productId || !sectionId) return;

    var url = baseUrl +
      '?section_id=' + encodeURIComponent(sectionId) +
      '&product_id=' + encodeURIComponent(productId) +
      '&limit=' + encodeURIComponent(limit) +
      '&intent=related';

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('Recommendations request failed');
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newSection = doc.querySelector('[data-you-may-also-like]');
        if (!newSection) return;

        var isReal = newSection.getAttribute('data-showing-real-recommendations') === 'true';
        var newGrid = newSection.querySelector('[data-product-grid]');

        // Only upgrade if Shopify actually returned real, non-empty
        // personalized recommendations — otherwise keep showing the
        // fallback collection that's already rendered and working.
        if (!isReal || !newGrid || !newGrid.children.length) return;

        // Stop the autoplay loop bound to the old (about to be
        // discarded) grid before replacing the DOM under it.
        if (root._ymalCarousel) {
          root._ymalCarousel.stop();
          root._ymalCarousel = null;
        }

        root.innerHTML = newSection.innerHTML;
        root.setAttribute('data-showing-real-recommendations', 'true');
        root.removeAttribute('hidden');
        initCardBehaviors(root);
      })
      .catch(function () {
        // Network hiccup, etc. — silently keep the fallback content.
      });
  }

  function initAll() {
    document.querySelectorAll('[data-you-may-also-like]').forEach(function (root) {
      if (root.dataset.ymalInit === 'true') return;
      root.dataset.ymalInit = 'true';

      // Fallback (or already-real) content is rendered server-side and
      // present immediately — wire up its behavior right away rather
      // than waiting on any network request.
      if (!root.hasAttribute('hidden')) {
        initCardBehaviors(root);
      }

      // If we're currently showing fallback content (not yet real
      // personalized recommendations), quietly try to upgrade in the
      // background. Harmless no-op if Shopify has nothing to offer.
      if (root.getAttribute('data-showing-real-recommendations') !== 'true') {
        fetchRealRecommendations(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-you-may-also-like]');
    if (root) {
      if (root._ymalCarousel) {
        root._ymalCarousel.stop();
        root._ymalCarousel = null;
      }
      root.dataset.ymalInit = '';
      initAll();
    }
  });
})();